import { useEffect, useId, useRef } from 'react';
import { pushLayer, popLayer, isTopLayer } from '../../layers';
import './workspace-overlay.css';

/**
 * Shared full-screen workspace overlay — single implementation of the
 * open/close/ESC/chrome pattern previously hand-rolled per workspace
 * (Wholesale had ESC-to-close; Automations didn't — this primitive adopts
 * Wholesale's behavior for both, see workspace-overlay.css header comment
 * for the geometry/chrome split this Phase-2 primitive intentionally keeps).
 *
 * Geometry (position/size/background) stays owned by each consumer via
 * `className` (e.g. `ws-panel` for Wholesale, `automations-workspace
 * glass-panel` for Automations) — this component + workspace-overlay.css
 * own only the shared head/title/close/body chrome vocabulary.
 *
 * `backdrop` (default true) renders the canonical anatomy scrim at
 * --z-overlay beneath the panel (--z-modal) — clicking it closes. Consumers
 * with full-viewport geometry (Wholesale) keep it on; it sits beneath their
 * opaque takeover, so it's inert visually but completes the anatomy + gives
 * backdrop-click-to-close for free. The docked Automations variant passes
 * `backdrop={false}` to preserve its approved chat-beside-panel look (a full
 * scrim would dim the chat and turn the drawer into a modal — see the
 * design-system.md workspace-overlay note on the docked variant).
 *
 * `backdrop` also gates the focus trap + focus restore, because it already
 * encodes exactly the distinction that decides whether trapping is correct: a
 * scrim means the rest of the app is unreachable, so focus must not leave the
 * panel. The docked drawer leaves chat usable beside it, so trapping there
 * would strand the keyboard inside a panel the user can still work around.
 */
export default function WorkspaceOverlay({ open, title, onClose, ariaLabel, children, className = '', backdrop = true }) {
  const layerId = useId();
  const panelRef = useRef(null);
  const restoreRef = useRef(null);

  // ESC is arbitrated by the shared layer stack: this overlay registers while
  // open and closes only when it is topmost, so an overlay stacked above it
  // (e.g. the Capabilities popup) consumes the keypress alone.
  useEffect(() => {
    if (!open) return undefined;
    pushLayer(layerId);
    const onKey = (e) => { if (e.key === 'Escape' && isTopLayer(layerId)) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => { popLayer(layerId); window.removeEventListener('keydown', onKey); };
  }, [open, onClose, layerId]);

  // Modal-only (see the `backdrop` note above): trap Tab inside the panel,
  // land focus on the close button, and hand focus back to whatever opened us.
  useEffect(() => {
    if (!open || !backdrop) return undefined;
    restoreRef.current = document.activeElement;
    // Close button rather than first content control: predictable, and never
    // inside content that may scroll.
    panelRef.current?.querySelector('.ws-overlay-close')?.focus();

    const onKey = (e) => {
      if (e.key !== 'Tab' || !isTopLayer(layerId)) return;
      const focusables = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables?.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // The trigger can be gone by now (its subtree may have unmounted while
      // the overlay was open) — restoring to a detached node is a no-op, so
      // check before calling and let focus fall back to the document.
      const restore = restoreRef.current;
      if (restore?.isConnected && typeof restore.focus === 'function') restore.focus();
    };
  }, [open, backdrop, layerId]);

  if (!open) return null;
  return (
    <>
      {backdrop && (
        <div className="ws-overlay-backdrop" onClick={() => onClose?.()} aria-hidden="true" />
      )}
      <aside
        ref={panelRef}
        className={`ws-overlay ${className}`}
        role="dialog"
        aria-modal={backdrop ? 'true' : undefined}
        aria-label={ariaLabel || title}
      >
        <div className="ws-overlay-head">
          <h2 className="ws-overlay-title">{title}</h2>
          <button
            type="button"
            className="ws-overlay-close"
            onClick={() => onClose?.()}
            aria-label={`Close ${title.toLowerCase()}`}
            title="Close"
          >
            ✕
          </button>
        </div>
        <div className="ws-overlay-body">{children}</div>
      </aside>
    </>
  );
}
